import React, { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import CheckValid from "../../Common/CheckValid";
import { SETTLE_OUTCOME, settlePartnerCoverRefund } from "../common/warrantyApi";
import { isAmbiguousFailure } from "../common/clientRequest";
import {
  pendingRequestId,
  recordAnswer,
  unresolvedFor,
  usePendingRequests,
} from "../common/pendingRequests";
import { formatMinor } from "../common/format";
import { ACTION_TEXT } from "../common/opsLabels";
import { rupeesToMinor } from "../Plans/editor/draftModel";

// Settle a cover refund the payment provider answered with a different amount. Staff check the
// provider dashboard, then choose one of two outcomes (no default): the provider refunded (with the
// amount it shows, in rupees) or nothing was refunded. A reason is required and the confirm button
// names the amount. Nothing is sent to the provider; the service records what staff saw.
//
// As every money action, the request id and what was sent are kept in `pendingRequests` keyed by
// action and target: after a 503 or a network failure (it may have been recorded) the dialog reopens
// with the same choices locked and confirming sends the same request, until the service answers
// definitely.
const ACTION = "settle_cover_refund";

const OUTCOMES = [
  { value: SETTLE_OUTCOME.REFUNDED, label: "The provider refunded" },
  { value: SETTLE_OUTCOME.NOT_REFUNDED, label: "Nothing was refunded" },
];

const SettleCoverRefundDialog = ({
  show,
  targetId,
  askedMinor,
  currency,
  subject,
  accessToken,
  onClose,
  onDone,
  onRefused,
  onUnsure,
}) => {
  usePendingRequests();
  const key = `${ACTION}:${targetId}`;
  // What an unconfirmed earlier try sent ({ outcome, amountText, reason }), or null.
  const held = unresolvedFor(key)?.reason || null;
  const [outcome, setOutcome] = useState(held ? held.outcome : null);
  const [amountText, setAmountText] = useState(held ? held.amountText : "");
  const [amountError, setAmountError] = useState("");
  const [reason, setReason] = useState(held ? held.reason : "");
  const [reasonError, setReasonError] = useState("");
  const [touched, setTouched] = useState(false);
  const [inFlight, setInFlight] = useState(false);
  const [failure, setFailure] = useState("");
  const [locked, setLocked] = useState(held !== null);
  // The amount field takes rupees; a cover in another currency (or none sent) cannot be settled as
  // refunded from here, so no amount is ever named that the server did not give.
  const rupees = currency === "INR";
  const money = rupees ? currency : null;

  const refunded = outcome === SETTLE_OUTCOME.REFUNDED;
  let amountValid = !refunded;
  if (refunded) {
    let message = "";
    CheckValid(amountText, {
      type: "warranty_rupees",
      label: "Amount refunded",
      positive: true,
      error: (m) => {
        message = m;
      },
    });
    amountValid = message === "";
  }
  const amountMinor = refunded && amountValid ? rupeesToMinor(amountText.trim()) : null;
  let reasonMessage = "";
  CheckValid(reason, {
    type: "warranty_reason",
    error: (m) => {
      reasonMessage = m;
    },
  });
  const reasonValid = reasonMessage === "";
  const valid =
    outcome !== null &&
    (!refunded || rupees) &&
    amountValid &&
    (!refunded || amountMinor !== null) &&
    reasonValid;
  const askedText =
    typeof askedMinor === "number" && money ? formatMinor(askedMinor, money) : "not sent by the service";

  const changeAmount = (value) => {
    if (locked) {
      return;
    }
    setAmountText(value);
    setFailure("");
    CheckValid(value, { type: "warranty_rupees", label: "Amount refunded", positive: true, error: setAmountError });
  };

  const changeReason = (value) => {
    if (locked) {
      return;
    }
    setReason(value);
    setFailure("");
    CheckValid(value, { type: "warranty_reason", error: setReasonError });
  };

  const confirm = async () => {
    setTouched(true);
    CheckValid(reason, { type: "warranty_reason", error: setReasonError });
    if (refunded) {
      CheckValid(amountText, {
        type: "warranty_rupees",
        label: "Amount refunded",
        positive: true,
        error: setAmountError,
      });
    }
    if (!valid || inFlight || !targetId) {
      return;
    }
    const trimmed = reason.trim();
    setInFlight(true);
    setFailure("");
    const result = await settlePartnerCoverRefund(
      accessToken,
      targetId,
      { outcome, providerAmountMinor: amountMinor },
      trimmed,
      pendingRequestId(key, JSON.stringify([ACTION, targetId, outcome, amountMinor, trimmed]))
    );
    const unsure = recordAnswer(key, result, { outcome, amountText, reason: trimmed });
    setInFlight(false);
    if (result.ok) {
      onDone(result.data);
      onClose();
      return;
    }
    setFailure(result.message);
    setLocked(unsure);
    // Only a definite refusal means the row moved; an ambiguous failure keeps the request as it is.
    if (onRefused && !isAmbiguousFailure(result)) {
      onRefused(result);
    }
  };

  const close = () => {
    if (unresolvedFor(key) && onUnsure) {
      onUnsure();
    }
    onClose();
  };

  const confirmation = !valid
    ? null
    : refunded
    ? `You are recording that the provider refunded ${formatMinor(amountMinor, money)} for this cover (amount asked: ${askedText}). The cover is then refunded, and a later cooling-off refund sends back the rest of the payment.`
    : "You are recording that no money went back to the rider. The cover can then be refunded again (Refund cover), under a new reference.";
  const confirmLabel = !valid
    ? "Record"
    : refunded
    ? `Record ${formatMinor(amountMinor, money)} refunded`
    : "Record nothing refunded";

  return (
    <Modal
      show={show}
      onHide={inFlight ? () => {} : close}
      backdrop={inFlight ? "static" : true}
      keyboard={!inFlight}
      className="commonModal"
      centered
    >
      <Modal.Header closeButton={!inFlight}>
        <Modal.Title>{ACTION_TEXT[ACTION].title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="warranty-dialog-hint">
          {subject ? `${subject}. ` : ""}
          {ACTION_TEXT[ACTION].effect} Amount asked for this cover: {askedText}.
        </p>
        <fieldset className="formloginset warranty-reason-codes">
          <legend>What does the provider dashboard show?</legend>
          {OUTCOMES.map((item) => (
            <Form.Check
              key={item.value}
              type="radio"
              id={`warranty-settle-outcome-${item.value}`}
              name="warrantySettleOutcome"
              label={item.label}
              checked={outcome === item.value}
              onChange={() => {
                setOutcome(item.value);
                setFailure("");
              }}
              disabled={inFlight || locked || (item.value === SETTLE_OUTCOME.REFUNDED && !rupees)}
            />
          ))}
        </fieldset>
        {!rupees && (
          <p className="warranty-muted" role="status">
            This cover is not in rupees, so a refunded amount cannot be recorded from here. Ask engineering.
          </p>
        )}
        <div className="formloginset">
          {refunded && (
            <Form.Group className="mb-2" controlId="warrantySettleAmount">
              <Form.Label>Amount the provider refunded (rupees)</Form.Label>
              <Form.Control
                type="text"
                inputMode="decimal"
                value={amountText}
                onChange={(event) => changeAmount(event.target.value)}
                onBlur={() => setTouched(true)}
                placeholder="For example 670.00"
                disabled={inFlight}
                readOnly={locked}
              />
              {touched && amountError && <div className="danger-colorset">{amountError}</div>}
            </Form.Group>
          )}
          <Form.Group className="mb-1" controlId="warrantySettleReason">
            <Form.Label>Reason</Form.Label>
            <Form.Control
              as="textarea"
              value={reason}
              maxLength={500}
              onChange={(event) => changeReason(event.target.value)}
              onBlur={() => setTouched(true)}
              placeholder="What the provider dashboard shows, and the refund id (kept in the audit log)"
              disabled={inFlight}
              readOnly={locked}
            />
            {touched && reasonError && <div className="danger-colorset">{reasonError}</div>}
          </Form.Group>
        </div>
        {confirmation && (
          <p className="warranty-dialog-hint" role="status">
            {confirmation}
          </p>
        )}
        {locked && !failure && (
          <div className="warranty-banner is-warning" role="status">
            <span>
              We could not confirm the earlier try. It may have been recorded. Confirming again sends the
              same request, so the choices cannot be changed.
            </span>
          </div>
        )}
        {failure && (
          <div className="warranty-dialog-error" role="alert">
            {failure}
            {locked && " It may have been recorded; confirming again repeats the same request safely."}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={close} disabled={inFlight}>
          Cancel
        </Button>
        <Button className="addbtncmn" onClick={confirm} disabled={!valid || inFlight}>
          {inFlight ? "Working" : confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SettleCoverRefundDialog;
